import { Drawer, Radio, Input, Tooltip, Space } from "antd";
import AIOffBadge from "./AIOffBadge";

const LOCKED_HINT =
  "Greyed options are not available for this section (set by the section library).";

/**
 * Bottom drawer for the selected section.
 *
 * The classification radios for disallowed classifications are DISABLED, not
 * hidden. This mirrors the data-layer enforcement (the backend validator
 * rejects saves that violate the section library's permitted_classifications).
 * The disabled state is a visual signal to the admin about what the section
 * library permits.
 *
 * Per `docs/spec/display-template-builder.md`, this drawer rises only when a
 * section in the canvas is selected.
 */
export default function SectionConfigurator({
  open,
  onClose,
  section,
  libraryEntry, // SectionLibraryEntry for this section_id
  onChange,
}) {
  if (!section) {
    return null;
  }

  const permitted = new Set(libraryEntry?.permitted_classifications || []);
  const styleOptions = libraryEntry?.style_options || [];

  const handleClassificationChange = (e) => {
    onChange?.({ classification: e.target.value });
  };

  const handleStyleChange = (e) => {
    onChange?.({ style: e.target.value });
  };

  return (
    <Drawer
      title={
        <span className="flex items-center gap-3">
          <span>Configure: {section.display_name || section.section_id}</span>
          {section.classification === "clinician_controlled" && <AIOffBadge />}
        </span>
      }
      placement="bottom"
      height="35vh"
      open={open}
      onClose={onClose}
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Classification
          </label>
          <Radio.Group
            value={section.classification}
            onChange={handleClassificationChange}
          >
            <Space direction="vertical">
              {["ai_driven", "hybrid", "clinician_controlled"].map((cls) => {
                const isPermitted = permitted.has(cls);
                const radio = (
                  <Radio key={cls} value={cls} disabled={!isPermitted}>
                    {labelFor(cls)}
                  </Radio>
                );
                return isPermitted ? (
                  radio
                ) : (
                  <Tooltip
                    key={cls}
                    title="Locked for this section by the section library."
                  >
                    <span>{radio}</span>
                  </Tooltip>
                );
              })}
            </Space>
          </Radio.Group>
          {permitted.size < 3 && (
            <div className="text-xs text-gray-400 mt-1">{LOCKED_HINT}</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
          <Radio.Group value={section.style} onChange={handleStyleChange}>
            <Space direction="vertical">
              {["verbose", "concise", "bullet_points"].map((s) => {
                const isPermitted = styleOptions.includes(s);
                const radio = (
                  <Radio key={s} value={s} disabled={!isPermitted}>
                    {styleLabel(s)}
                  </Radio>
                );
                return isPermitted ? (
                  radio
                ) : (
                  <Tooltip
                    key={s}
                    title="Locked for this section by the section library."
                  >
                    <span>{radio}</span>
                  </Tooltip>
                );
              })}
            </Space>
          </Radio.Group>
          {styleOptions.length < 3 && (
            <div className="text-xs text-gray-400 mt-1">{LOCKED_HINT}</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AMD field name
            </label>
            <Input
              value={section.amd_field_name || ""}
              onChange={(e) => onChange?.({ amd_field_name: e.target.value })}
              placeholder="e.g. WL_HPI"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AMD control_id
            </label>
            <Input
              value={section.amd_control_id || ""}
              onChange={(e) =>
                onChange?.({ amd_control_id: e.target.value || null })
              }
              placeholder="Filled when template is provisioned in AMD"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

function labelFor(classification) {
  switch (classification) {
    case "ai_driven":
      return "AI-driven";
    case "hybrid":
      return "Hybrid (AI suggests, clinician accepts)";
    case "clinician_controlled":
      return "Clinician only";
    default:
      return classification;
  }
}

function styleLabel(style) {
  switch (style) {
    case "verbose":
      return "Verbose";
    case "concise":
      return "Concise";
    case "bullet_points":
      return "Bullet points";
    default:
      return style;
  }
}
